import { ChatState } from "./definitions";

/*
 * "Modo humano" = a conversa foi transbordada e um atendente humano assumiu.
 * Nesse estado o widget NUNCA mostra o balão de "bot digitando" nem trava o input.
 *
 * Sinal primário: conversation.attributes.handoff === true (carimbado pelo synccx-api no
 *   transbordo) → vira chat.humanMode via o conversationListener.
 * Reforço (fallback): entrou um participante que não é o cliente (o atendente do Flex vira
 *   participant ao aceitar). Assume que o bot NÃO é participant da conversa (só o cliente é,
 *   até o atendente entrar) — se isso mudar, este fallback precisa de ajuste.
 */
export const isHumanMode = (chat: ChatState): boolean => {
    if (chat.humanMode) {
        return true;
    }
    const currentUserId = chat.conversationsClient?.user?.identity;
    return Boolean(chat.participants?.some((p) => p.identity && p.identity !== currentUserId));
};

/*
 * "A conversa acabou" — a regra única, lida por QUEM PRECISA DECIDIR SE AINDA DÁ PRA FALAR.
 *
 * Vivia inline no `MessagingCanvasPhase`. Virou selector em 2026-09-03, quando passou a ter um
 * segundo consumidor (`MessageBubble`): duas cópias da mesma condição divergem, e divergir aqui
 * significa ou prender o apostador numa tela morta, ou deixá-lo falar num atendimento que a
 * operação não enxerga.
 *
 * `closed` sempre encerra. `inactive` encerra só na fase de IA: no transbordo o synccx-api estende o
 * `Timers.Inactive`, e este guard é defesa em profundidade — atendimento humano NUNCA é cortado por
 * timer, independente do que o backend faça.
 *
 * 🔴 `undefined` conta como VIVA, de propósito. Acontece na sessão restaurada antes do primeiro
 * evento `updated` do SDK. Tratar como encerrada mataria os botões de quem está em atendimento
 * normal — o oposto do que este selector existe para proteger.
 */
export const isConversationEnded = (chat: ChatState): boolean =>
    chat.conversationState === "closed" || (chat.conversationState === "inactive" && !isHumanMode(chat));

/*
 * Onde o card da pesquisa monta — a regra em UM lugar, para os dois consumidores.
 *
 * Até 2026-09-12 não havia decisão a tomar: o card ocupava o lugar do composer, então avaliar e
 * conversar eram mutuamente exclusivos por CONSTRUÇÃO. Com o disparo por macro no meio do
 * atendimento, os dois passam a caber na tela ao mesmo tempo, e aí existe escolha — que é regra de
 * estado, não de componente. Em duas cópias ela divergiria, como já divergiu o `isConversationEnded`.
 *
 * `csatPending` é só o MOMENTO de perguntar. Se existe pesquisa de verdade quem responde é o GET
 * autoritativo, e o card se barra sozinho por `requested`. Estes selectors de propósito NÃO olham
 * `chat.csat` para decidir montar durante o atendimento: olhar exigiria ter a pesquisa para poder ir
 * buscar a pesquisa.
 */
export const deveMostrarCsatNoAtendimento = (chat: ChatState): boolean => {
    if (isConversationEnded(chat)) {
        return false;
    }

    /*
     * O desfecho que o card acabou de produzir vence o sinal — e o sinal aqui já está DESLIGADO.
     *
     * O synccx apaga `csatPending` no mesmo instante em que grava a nota. Como a faixa montava só
     * por esse sinal, o desligamento desmontava o card inteiro e levava junto o "Obrigado" que ele
     * tinha acabado de renderizar: o apostador avaliava e a tela não confirmava nada. Medido em
     * 2026-09-22, antes de existir conserto.
     *
     * ⚠️ Isto NÃO contradiz a regra do bloco acima, de não olhar `chat.csat` para decidir montar.
     * Aquela regra protege contra a dependência circular de precisar da pesquisa para ir buscar a
     * pesquisa, e vale no caminho de ABRIR a faixa. Aqui a pesquisa já está no estado — ela só
     * chegou a `submitted`/`readonly` porque o POST voltou. Não há círculo a fechar.
     *
     * `respondidaNoAtendimento` é o que impede um `submitted` da despedida de ressuscitar uma faixa
     * dentro de uma conversa viva, e `faixaAposentada` é o que separa "se mantém" de "fica para
     * sempre" — quem o carimba é o próprio card, depois de o agradecimento ter sido visto.
     */
    const desfecho = chat.csat?.status;
    if ((desfecho === "submitted" || desfecho === "readonly") && chat.csat?.respondidaNoAtendimento === true) {
        return chat.csat?.faixaAposentada !== true;
    }

    return chat.csatPending === true;
};

/*
 * A despedida, que é o caminho que já existia — e continua idêntico para quem nunca recebeu sinal.
 *
 * A única exclusão é quem RESPONDEU no meio do atendimento: essa pessoa não vê nada aqui, nem a
 * escala nem o agradecimento. O agradecimento é retorno de uma ação que a pessoa acabou de fazer; na
 * despedida ela não fez nada. Quem recarrega a página depois de responder já via `null` (o seed volta
 * com `answered` e o card se barra), então isto alinha os dois caminhos em vez de inventar um terceiro.
 *
 * Quem IGNOROU no meio continua sendo perguntado — decisão do Mateus, 2026-09-12.
 *
 * De propósito NÃO reconfere `isConversationEnded`: quem monta a despedida é o `MessagingCanvasPhase`,
 * e essa decisão é dele. Reconferir aqui seria a segunda cópia da condição — o defeito que fez aquela
 * regra virar selector em 2026-09-03. A primeira versão desta linha tinha a cópia, e ela derrubou um
 * teste que monta a despedida sem estado de conversa.
 */
export const deveMostrarCsatNaDespedida = (chat: ChatState): boolean =>
    chat.csat?.respondidaNoAtendimento !== true;
